import {
  Box,
  Button,
  Divider,
  Icons,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@ublend-npm/aulaui-core';
import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStyles } from './AssignmentHeader.styles';
import { AssignmentHeaderPage } from '../types';
import {
  Assignment,
  ProviderType,
  ResponsiveAssignment,
} from '@core/assignments/types';
import formatDate from '@core/utils/formatDate';
import { EDITED, VIEW_SUBMISSIONS, YET_TO_RELEASE } from '@app/constants/texts';
import { getAssignmentsMaterialsBackQuery } from '@core/constants/backButtonNavigation';
import {
  TURNITIN_PROVIDER,
  HANDIN_PROVIDER,
  QUIZZES_PROVIDER,
  LTI1P3_PROVIDER,
  MANUAL,
  OVERVIEW_PAGE,
  SUBMISSIONS_PAGE,
} from '@core/assignments/constants';
import {
  accessToken as getAccessToken,
  currentUser,
} from '@core/selectors/user';
import showToast from '@core/toasts/operations/showToast.operation';
import launchTurnitinAssignment from '@app/actions/assignments/launchTurnitinAssignment.action';
import TurnitinModal from '../TurnitinModal';
import { Lti1p3Launch } from '../../Assignments/Lti1p3Launch/Lti1p3Launch';
import {
  getAssignmentProvider,
  launchHandin,
  launchLti1p3,
  launchQuizzes,
  launchTurnitin,
  resolveHandinExternalId,
} from '../utils/launchAssignment';
import SubmissionsDownloadButton from './SubmissionDownload/SubmissionsDownloadButton';
import ConversionSection from '../LinkExtension/ConversionSection';
import AssignmentTypeProvider, {
  useAssignmentTypeProvider,
} from '../SubmissionsResponsive/AssignmentTypeProvider';
import { AulaState } from '@core/types/state';
import {
  getAssignmentSubmissions,
  hasFetchedSubmissions,
  isFetchingSubmissions,
} from '@core/assignments/selectors';
import { operations as assignmentOperations } from '../../../../../store/assignments';
import useIsClamped from '@app/hooks/useIsClamped';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import launchManualAssignment from '../../Assignments/utils/launchManualAssignment';
import getAssignmentPropertiesDiff from '../utils/getAssignmentPropertiesDiff';
import AssignmentMaterials from '../AssignmentMaterials/AssignmentMaterials';
import LinkedMaterials from '../AssignmentCard/LinkedMaterials/LinkedMaterials';
import { goToMaterials } from '@app/actions/navigation';
import { SubmissionAssignment } from '../SubmissionsResponsive/SubmissionsResponsive';
import AssignmentSpaceInfo from '../utils/AssignmentSpaceInfo';
import { getCurrentSpaceName } from '@core/selectors/space';
import { GRADE_TYPES, getProvidersWithViewButton } from '../constants';
import {
  formatGroupSubmissions,
  groupSubmissionsByDate,
} from '../SubmissionsResponsive/GroupSubmissions/formatGroupSubmissions';
import { getLtiProviders } from '@core/selectors/lti';
import { isAssignmentProviderDisabled } from '@core/assignments/utils';

type EditableDateProperty = 'startDate' | 'endDate' | 'gradesReleasedAt';

interface AssignmentHeaderProps {
  assignment: Assignment;
  parentAssignment?: SubmissionAssignment;
  page: AssignmentHeaderPage;
  handleViewSubmissions?: () => void;
  isEducator: boolean;
  accessingAsStaff: boolean;
  currentSpaceId: string;
  totalStudentCount?: number;
  totalSubmissionCount?: number;
  isStudent?: boolean;
  submissionCohortName?: string;
  onMaterialListClick?: (assignmentId: string) => void;
}
const ASSIGNMENT_HEADER_TITLE_ID = 'assignment-header-title';
interface OpenInProviderButtonProps {
  styles: any;
  provider: ProviderType;
  onClick: () => void;
  isMobile: boolean;
  isStudent?: boolean;
  accessingAsStaff: boolean;
  providersWithViewButton: ReturnType<typeof getProvidersWithViewButton>;
}

// Open in Provider button
const OpenInProviderButton = ({
  styles,
  provider,
  onClick,
  isMobile,
  isStudent,
  accessingAsStaff,
  providersWithViewButton,
}: OpenInProviderButtonProps) => {
  // Get the details from the context for open in button
  const {
    type,
    title: PROVIDER_TITLE,
    renderImage,
  } = useAssignmentTypeProvider();

  // To check if button text exceeds the button width
  const [openLTIButtonRef, isTextOverflowing] =
    useIsOverflowing<HTMLDivElement>();
  const providerButtonLabel = `Open in ${PROVIDER_TITLE}`;
  const showProviderButtonTooltip = isTextOverflowing && !isMobile;
  const isTurnitinDisabledForStaff =
    accessingAsStaff && type === TURNITIN_PROVIDER;

  // If the provider is manual and the user is a student or staff, we don't show the button
  if ((type === MANUAL && isStudent) || (type === MANUAL && accessingAsStaff)) {
    return null;
  }

  // If the provider is quizzes, we don't show the button
  if (type === QUIZZES_PROVIDER) {
    return null;
  }

  return (
    <Tooltip
      placement="bottom"
      disableInteractive
      title={providerButtonLabel}
      disableFocusListener={!showProviderButtonTooltip}
      disableHoverListener={!showProviderButtonTooltip}
      disableTouchListener={!showProviderButtonTooltip}
    >
      <Button
        type="button"
        kind="secondary"
        sx={{
          ...styles.providerButton,
          color: providersWithViewButton[provider]?.accent || 'default',
          borderColor: providersWithViewButton[provider]?.accent || 'default',
          '&:hover': {
            backgroundColor:
              providersWithViewButton[provider]?.hoverBg || 'default',
            borderColor: providersWithViewButton[provider]?.accent || 'default',
          },
        }}
        onClick={onClick}
        disabled={isTurnitinDisabledForStaff}
      >
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: 'inline-flex',
            opacity: isTurnitinDisabledForStaff ? 0.5 : 1,
          }}
        >
          {renderImage({})}
        </Box>
        <Box sx={styles.providerButtonText} ref={openLTIButtonRef}>
          {providerButtonLabel}
        </Box>
        <Icons.OpenInNew fontSize="small" aria-hidden />
      </Button>
    </Tooltip>
  );
};

interface HeaderTitleProps {
  title?: string;
  titleRef: (node: HTMLDivElement | null) => void;
  showTooltip: boolean;
  sx: React.ComponentProps<typeof Box>['sx'];
  wrapperSx: any;
}

// Main title for the assignment header
const HeaderTitle = ({
  title,
  titleRef,
  showTooltip,
  sx,
  wrapperSx,
}: HeaderTitleProps) => (
  <Box sx={wrapperSx}>
    <Tooltip
      placement="bottom"
      disableInteractive
      title={title ?? ''}
      disableFocusListener={!showTooltip}
      disableHoverListener={!showTooltip}
      disableTouchListener={!showTooltip}
    >
      <Box ref={titleRef} id={ASSIGNMENT_HEADER_TITLE_ID} sx={sx}>
        {title}
      </Box>
    </Tooltip>
  </Box>
);
interface TitleWithCountProps {
  title?: string;
  count: string;
  isMobile: boolean;
  sx: React.ComponentProps<typeof Box>['sx'];
  showConversionSection: boolean;
  assignment: Assignment;
  styles: any;
  countSx: React.ComponentProps<typeof Box>['sx'];
  wrapperSx: any;
}

const TITLE_MAX_LINES = 2;
const TITLE_LINE_HEIGHT = 1.6;
const TITLE_TEXT_ATTRIBUTE = 'data-title-text';

// Title spans the full width and is limited to 2 lines. The count is inline, so
// it always sits at the end of the last line, and the title is trimmed with "…"
// when the two of them cannot fit together.
const HeaderTitleWithCount = ({
  title = '',
  count,
  isMobile,
  sx,
  styles,
  countSx,
  wrapperSx,
}: TitleWithCountProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [visibleTitle, setVisibleTitle] = useState(title);
  const rafRef = useRef<number | null>(null);

  // Measure on an offscreen copy appended to body so we never disturb the
  // observed container (avoids "ResizeObserver loop" warnings).
  const fit = useCallback(() => {
    const container = containerRef.current;
    if (!container || container.clientWidth === 0) {
      return;
    }

    const computedStyles = window.getComputedStyle(container);
    const lineHeight = parseFloat(computedStyles.lineHeight);
    if (!lineHeight) {
      return;
    }
    const maxHeight = lineHeight * TITLE_MAX_LINES + 1;

    const clone = container.cloneNode(true) as HTMLElement;
    const cloneText = clone.querySelector<HTMLElement>(
      `[${TITLE_TEXT_ATTRIBUTE}]`
    );
    if (!cloneText) {
      return;
    }
    const cloneTitleSx = styles.cloneTitleSx;
    Object.assign(clone.style, {
      ...cloneTitleSx,
      width: `${container.clientWidth}px`,
    });
    clone.removeAttribute('id');
    document.body.appendChild(clone);

    const fits = (text: string) => {
      cloneText.textContent = text;
      return clone.offsetHeight <= maxHeight;
    };

    let fittedTitle = title;
    if (!fits(title)) {
      // Longest prefix of the title that still leaves room for the count
      let low = 0;
      let high = title.length;
      while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        if (fits(`${title.slice(0, mid).trimEnd()}…`)) {
          low = mid;
        } else {
          high = mid - 1;
        }
      }
      fittedTitle = low > 0 ? `${title.slice(0, low).trimEnd()}…` : '…';
    }

    document.body.removeChild(clone);
    setVisibleTitle((prev) => (prev === fittedTitle ? prev : fittedTitle));
  }, [title, count, isMobile]);

  // Show the full title until fit() trims it for the current width
  useLayoutEffect(() => {
    setVisibleTitle(title);
    fit();
  }, [title, fit]);

  // Re-fit when the available width changes (resize, sidebar toggle, etc.).
  // Defer with rAF so layout work never runs inside the ResizeObserver
  // delivery cycle (that triggers the "undelivered notifications" error).
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    let lastWidth = container.clientWidth;
    const observer = new ResizeObserver(() => {
      const nextWidth = container.clientWidth;
      if (nextWidth === lastWidth) {
        return;
      }
      lastWidth = nextWidth;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        fit();
      });
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [fit]);

  const isTrimmed = visibleTitle !== title;

  return (
    <Box sx={wrapperSx}>
      <Tooltip
        placement="bottom"
        disableInteractive
        title={title}
        disableFocusListener={!isTrimmed || isMobile}
        disableHoverListener={!isTrimmed || isMobile}
        disableTouchListener={!isTrimmed || isMobile}
      >
        <Box
          ref={containerRef}
          id={ASSIGNMENT_HEADER_TITLE_ID}
          sx={{
            ...sx,
            lineHeight: TITLE_LINE_HEIGHT,
            maxHeight: `${TITLE_LINE_HEIGHT * TITLE_MAX_LINES}em`,
          }}
        >
          <Box component="span" {...{ [TITLE_TEXT_ATTRIBUTE]: '' }}>
            {visibleTitle}
          </Box>
          <Box
            component="span"
            sx={{
              ...countSx,
            }}
          >
            {` ${count}`}
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

const AssignmentHeader = ({
  assignment,
  parentAssignment,
  page,
  handleViewSubmissions,
  isEducator,
  accessingAsStaff,
  currentSpaceId,
  totalStudentCount,
  totalSubmissionCount,
  isStudent,
  submissionCohortName,
  onMaterialListClick,
}: AssignmentHeaderProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const dispatch = useDispatch();
  const user = useSelector(currentUser);
  const ltiProviders = useSelector(getLtiProviders);
  // States for turnitin and LTI 1.3 launch
  const [turnitinLaunch, setTurnitinLaunch] = useState<Assignment | null>(null);
  const [lti1p3Assignment, setLti1p3Assignment] = useState<Assignment | null>(
    null
  );
  // States for to show linked materials list in a modal
  const [openMaterials, setOpenMaterials] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme?.palette?.mode === 'dark';
  const assignmentId = assignment?.id || assignment?.objectId;
  const currentSpaceName = useSelector((state: AulaState) =>
    getCurrentSpaceName(state)
  );

  const accessToken = useSelector((state: AulaState) =>
    getAccessToken(state, currentSpaceId)
  );

  // Check if the assignment is a group assignment
  const isGroupAssignment = Boolean(
    assignment && (assignment as ResponsiveAssignment)?.groupSet
  );

  const isAnonymised = assignment.isAnonymised;
  const hasExtensions = Boolean(
    assignment?.extensions?.length ||
      parentAssignment?.extensions?.length ||
      assignment?.parentAssignmentId
  );
  const hasFetchedAssignmentSubmissions = useSelector((state: AulaState) =>
    assignmentId ? hasFetchedSubmissions(state, assignmentId) : false
  );
  const isFetchingAssignmentSubmissions = useSelector((state: AulaState) =>
    assignmentId ? isFetchingSubmissions(state, assignmentId) : false
  );
  const submissionCount = useSelector((state: AulaState) => {
    if (!assignmentId) {
      return 0;
    }
    // Get all submissions
    const submissions = getAssignmentSubmissions(state, assignmentId);
    const submissionsData = isGroupAssignment
      ? formatGroupSubmissions(submissions)
      : submissions;
    const { earlySubmissions, lateSubmissions } = groupSubmissionsByDate(
      submissionsData,
      assignment?.endDate || ''
    );

    // Students or groups with no submission are excluded, matching the legacy
    return earlySubmissions?.length + lateSubmissions?.length;
  });

  useEffect(() => {
    if (
      !isEducator ||
      !accessToken ||
      !assignmentId ||
      getAssignmentProvider(assignment) !== HANDIN_PROVIDER
    ) {
      return;
    }

    if (hasFetchedAssignmentSubmissions || isFetchingAssignmentSubmissions) {
      return;
    }

    dispatch(
      assignmentOperations.fetchSubmissions({
        accessToken,
        assignmentId,
      })
    );
  }, [
    accessToken,
    assignment,
    assignmentId,
    dispatch,
    hasFetchedAssignmentSubmissions,
    isEducator,
    isFetchingAssignmentSubmissions,
  ]);

  const {
    title,
    startDate,
    endDate,
    gradeType,
    gradesReleasedAt,
    linkedMaterials: assignmentLinkedMaterials,
    provider,
  } = assignment;

  // Get the linked materials for the assignment and first material
  const linkedMaterials = assignmentLinkedMaterials ?? [];
  const firstMaterial = linkedMaterials[0];
  const additionalMaterialsCount = Math.max(0, linkedMaterials.length - 1);
  const isMultiMaterials = linkedMaterials.length > 1;
  const isQuizSubmission = assignment?.provider === QUIZZES_PROVIDER;
  const isLti1P3Provider = assignment?.provider === LTI1P3_PROVIDER;
  const isManualProvider = assignment?.provider === MANUAL;
  const isConversionToExtensionPermitted =
    !hasExtensions && !isGroupAssignment && !isAnonymised;
  const showConversionSection = Boolean(
    assignment &&
      !isQuizSubmission &&
      !isLti1P3Provider &&
      !isManualProvider &&
      isEducator &&
      isConversionToExtensionPermitted
  );

  // function to handle the view linked materials ( both single and multiple linked materials)
  const handleViewLinkedMaterials = () => {
    // this function stores last visited assignment id in session storage
    const notifyMaterialListClick = () => {
      if (assignmentId && onMaterialListClick) {
        onMaterialListClick(assignmentId);
      }
    };

    if (isMultiMaterials) {
      setOpenMaterials(true);
      notifyMaterialListClick();
      return;
    }

    if (!firstMaterial?.id || !currentSpaceId) {
      return;
    }

    notifyMaterialListClick();
    dispatch(
      goToMaterials(
        currentSpaceId,
        firstMaterial.id,
        getAssignmentsMaterialsBackQuery(assignmentId)
      )
    );
  };

  // function to handle the click event for the linked materials
  const handleLinkedMaterialsClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    event.stopPropagation();
    handleViewLinkedMaterials();
  };

  // handle keyboard event for the linked materials
  const handleLinkedMaterialsKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleViewLinkedMaterials();
  };

  // Get the modified date properties for the assignment
  const modifiedDateProperties = useMemo(
    () =>
      parentAssignment
        ? getAssignmentPropertiesDiff(assignment, parentAssignment)
        : {},
    [assignment, parentAssignment]
  );

  // Get the date info for assignments along with Edited tag if applicable
  const getDateMetadataValue = (
    property: EditableDateProperty,
    date?: string,
    fallback = 'NA'
  ): React.ReactNode => {
    if (!date) {
      return fallback;
    }

    const hasBeenModified = modifiedDateProperties[property];

    return (
      <Box component="span" sx={styles.metadataValueRow}>
        <span>{formatDate(date)}</span>
        {hasBeenModified ? (
          <Typography component="div" sx={styles.metadataEdited}>
            {EDITED}
          </Typography>
        ) : null}
      </Box>
    );
  };

  // These are the LTI providers having a view button in UI
  const PROVIDERS_WITH_VIEW_BUTTON = getProvidersWithViewButton(isDark);

  const PROVIDER_KEYS = Object.keys(PROVIDERS_WITH_VIEW_BUTTON);
  const assignmentProvider = getAssignmentProvider(assignment);

  // Check if the user can view the submissions button
  const canViewSubmissionsButton =
    Boolean(currentSpaceId && assignmentId) &&
    page === OVERVIEW_PAGE &&
    PROVIDER_KEYS.includes(provider);

  const GRADE_TYPE_LABEL =
    GRADE_TYPES[gradeType as keyof typeof GRADE_TYPES] || gradeType || 'NA';

  const ASSIGNMENT_METADATA = useMemo(() => {
    return [
      startDate
        ? {
            id: 'open-date',
            label: 'Open date',
            value: getDateMetadataValue('startDate', startDate),
            icon: (
              <Icons.CalendarMonthOutlined
                sx={{ fontSize: '24px' }}
                aria-hidden
              />
            ),
          }
        : null,
      endDate
        ? {
            id: 'due-date',
            label: 'Due date',
            value: getDateMetadataValue('endDate', endDate),
            icon: (
              <Icons.CalendarMonthOutlined
                sx={{ fontSize: '24px' }}
                aria-hidden
              />
            ),
          }
        : null,
      {
        id: 'grades-release-date',
        label: 'Grades release date',
        value: getDateMetadataValue(
          'gradesReleasedAt',
          gradesReleasedAt,
          YET_TO_RELEASE
        ),
        icon: <Icons.ChecklistOutlined sx={{ fontSize: '24px' }} aria-hidden />,
      },
      {
        id: 'grade-type',
        label: 'Grade type',
        value: GRADE_TYPE_LABEL,
        icon: <Icons.StarBorder sx={{ fontSize: '24px' }} aria-hidden />,
      },
    ];
  }, [
    endDate,
    gradesReleasedAt,
    GRADE_TYPE_LABEL,
    modifiedDateProperties,
    startDate,
  ]);

  // Text overflow check for Title and description
  const [titleRef, isTitleClamped] = useIsClamped<HTMLDivElement>();

  // Launch assignments based on provider
  const launchAssignment = async () => {
    if (!assignment) {
      return;
    }
    const spaceId = assignment?.classRoom?.objectId || currentSpaceId;

    switch (getAssignmentProvider(assignment)) {
      case HANDIN_PROVIDER:
        launchHandin({
          assignmentId:
            assignment?.externalId ||
            (await resolveHandinExternalId(assignment, spaceId)),
          spaceId,
        });
        break;
      case TURNITIN_PROVIDER:
        if (accessingAsStaff) {
          return;
        }
        if (isEducator && user) {
          launchTurnitinAssignment({
            user,
            assignment,
            spaceId,
          });
          break;
        }
        launchTurnitin(assignment, setTurnitinLaunch);
        break;
      case QUIZZES_PROVIDER:
        launchQuizzes({ assignment, spaceId });
        break;
      case LTI1P3_PROVIDER:
        launchLti1p3(assignment, setLti1p3Assignment);
        break;
      case MANUAL:
        launchManualAssignment({ assignmentId, spaceId });
        break;
      default:
        break;
    }
  };

  // Handle view submission button click
  const onViewSubmissions = () => {
    if (!canViewSubmissionsButton || !handleViewSubmissions) {
      return;
    }
    if (isEducator || accessingAsStaff) {
      handleViewSubmissions();
      return;
    }
  };

  // Get the submission count vs total students count
  const submissionCountVsTotal =
    totalStudentCount === 0 || !totalStudentCount
      ? '0 students'
      : `${totalSubmissionCount || 0}/${totalStudentCount || 0} submissions`;

  // Header title
  const HEADER_TITLE = page === OVERVIEW_PAGE ? title : submissionCohortName;
  // Check if provider button is disabled or not
  const isProviderDisabled = isAssignmentProviderDisabled(
    assignment,
    ltiProviders
  );
  // Show provider button
  const showProvider =
    assignment.provider !== QUIZZES_PROVIDER && !isProviderDisabled;

  // Condition to show a seperator after listing all meta data's
  const showDivider = showProvider
    ? provider !== MANUAL || isEducator
    : provider === HANDIN_PROVIDER || provider === TURNITIN_PROVIDER;

  return (
    <>
      <Box
        sx={styles.headerCard}
        role="region"
        aria-labelledby={ASSIGNMENT_HEADER_TITLE_ID}
      >
        {isMobile && (
          <Box sx={{ mb: '0.5rem' }}>
            <AssignmentSpaceInfo
              spaceName={currentSpaceName || null}
              theme={theme}
            />
          </Box>
        )}
        {page === OVERVIEW_PAGE && (
          <HeaderTitle
            title={HEADER_TITLE}
            titleRef={titleRef}
            showTooltip={isTitleClamped && !isMobile}
            sx={{ ...styles.title, ...(isMobile ? styles.titleMobile : {}) }}
            wrapperSx={styles.headerWrapper}
          />
        )}
        {page === SUBMISSIONS_PAGE && (
          <HeaderTitleWithCount
            title={HEADER_TITLE}
            count={`(${submissionCountVsTotal})`}
            isMobile={isMobile}
            sx={styles.submissionPageTitle}
            showConversionSection={showConversionSection}
            assignment={assignment}
            styles={styles}
            wrapperSx={styles.submissionTitleRow}
            countSx={styles.submissionCount}
          />
        )}

        {firstMaterial && page === OVERVIEW_PAGE && (
          <LinkedMaterials
            containerSx={{ ...styles.linkedMaterialTab }}
            title={firstMaterial?.title ?? null}
            onClick={handleLinkedMaterialsClick}
            onKeyDown={handleLinkedMaterialsKeyDown}
            remainingMaterialCount={additionalMaterialsCount}
          />
        )}

        <Box
          sx={styles.metadataRow}
          role="list"
          aria-label="Assignment details"
        >
          {ASSIGNMENT_METADATA.filter(Boolean).map((item) => (
            <Box key={item?.id} sx={styles.metadataItem} role="listitem">
              <Box sx={styles.metadataIcon} aria-hidden="true">
                {item?.icon}
              </Box>
              <Box>
                <Typography sx={styles.metadataLabel}>{item?.label}</Typography>
                <Typography sx={styles.metadataValue}>{item?.value}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {showDivider && <Divider sx={styles.divider} aria-hidden="true" />}
        <Box
          id="button-container"
          className={isEducator ? 'educatorView' : 'studentView'}
          role="group"
          aria-label="Assignment actions"
          sx={styles.actionsRow}
        >
          <AssignmentTypeProvider
            assignment={assignment}
            spaceId={currentSpaceId}
            user={user ?? undefined}
          >
            {showProvider && (
              <OpenInProviderButton
                styles={styles}
                provider={(assignmentProvider || provider) as ProviderType}
                onClick={() => launchAssignment()}
                isMobile={isMobile}
                isStudent={isStudent}
                accessingAsStaff={accessingAsStaff}
                providersWithViewButton={PROVIDERS_WITH_VIEW_BUTTON}
              />
            )}
            {(isEducator || accessingAsStaff) && (
              <>
                {getAssignmentProvider(assignment) === HANDIN_PROVIDER && (
                  <SubmissionsDownloadButton
                    styles={styles}
                    currentUserIsEducator={isEducator}
                    assignmentExternalId={assignment.externalId}
                    accessToken={accessToken || ''}
                    isGroupAssignment={isGroupAssignment}
                    isAnonymisedAssignment={isAnonymised}
                    submissionCount={submissionCount}
                  />
                )}
                {canViewSubmissionsButton && (
                  <Button
                    type="button"
                    kind="secondary"
                    sx={styles.purpleButton}
                    onClick={onViewSubmissions}
                    disabled={isGroupAssignment}
                  >
                    {VIEW_SUBMISSIONS}
                  </Button>
                )}
                {showConversionSection && (
                  <ConversionSection assignment={assignment} styles={styles} />
                )}
              </>
            )}
          </AssignmentTypeProvider>
        </Box>
      </Box>
      {turnitinLaunch && (
        <TurnitinModal
          assignment={{
            ...turnitinLaunch,
            objectId: turnitinLaunch?.objectId || turnitinLaunch?.id,
            title: turnitinLaunch?.title,
            description: turnitinLaunch?.description || '',
            endDate: turnitinLaunch?.endDate || '',
            startDate: turnitinLaunch?.startDate || '',
            gradesReleasedAt: turnitinLaunch?.gradesReleasedAt || '',
            maxGrade: turnitinLaunch?.maxGrade,
          }}
          user={user ?? undefined}
          space={{ objectId: currentSpaceId }}
          isEducator={isEducator}
          onClose={() => setTurnitinLaunch(null)}
        />
      )}
      {openMaterials && (
        <AssignmentMaterials
          returnAssignment={assignmentId}
          open={openMaterials}
          onClose={() => setOpenMaterials(false)}
          linkedMaterials={linkedMaterials}
        />
      )}
      {lti1p3Assignment?.providerId && lti1p3Assignment?.resourceLinkId && (
        <Lti1p3Launch
          providerId={lti1p3Assignment?.providerId}
          resourceLinkId={lti1p3Assignment?.resourceLinkId}
          resourceLinkTitle={lti1p3Assignment?.title}
          assignmentId={lti1p3Assignment?.id || lti1p3Assignment?.objectId}
          onSuccessLaunch={() => setLti1p3Assignment(null)}
          onErrorLaunch={() => {
            setLti1p3Assignment(null);
            dispatch(
              showToast({
                message: 'There was an error when trying to open the LTI tool.',
              })
            );
          }}
        />
      )}
    </>
  );
};

export default AssignmentHeader;
